import { z } from "zod";
import Connector, {
  ConnectorSecret,
  connectorSecretSchema,
  EditDefaultValuesGetter,
  IConnectorRow,
} from "./Connector";
import { ReactNode } from "react";
import { UseFormRegister, Control, FieldErrors } from "react-hook-form";
import Five9ConfigForm from "../forms/Five9ConfigForm";
import { ConnectorWizardFormValues } from "../forms/ConnectorWizard";

export interface Five9ConnectorRow
  extends Omit<IConnectorRow, "connectorDetails"> {
  connectorDetails: {
    Username: ConnectorSecret;
    Password: ConnectorSecret;
    FolderName: string;
    ReportName: string;
  };
}

export const five9Schema = z.object({
  username: connectorSecretSchema({
    required: true,
    requiredMessage: "five9Connector:usernameRequired",
  }),
  password: connectorSecretSchema({
    required: true,
    requiredMessage: "five9Connector:passwordRequired",
  }),
  reportName: z
    .string({
      required_error: "five9Connector:reportNameRequired",
    })
    .min(1, {
      message: "five9Connector:reportNameRequired",
    }),
  folderName: z
    .string({
      required_error: "five9Connector.folderNameRequired",
    })
    .min(1, {
      message: "five9Connector:folderNameRequired",
    }),
});
export type Five9FormValues = z.infer<typeof five9Schema>;

export default class Five9Connector
  implements Connector<Five9FormValues, Five9ConnectorRow>
{
  public static readonly Type = "Five9";

  public get fields(): string[] {
    return [
      ...five9Schema
        .keyof()
        .options.map((key) => `${Five9Connector.Type}.${key}`),
    ];
  }

  public getEditDefaultValues: EditDefaultValuesGetter<
    Five9FormValues,
    Five9ConnectorRow
  > = (connector?: Five9ConnectorRow): Five9FormValues => {
    return {
      username: connector?.connectorDetails?.Username ?? {
        value: "",
        isSensitive: false,
        hasValue: false,
      },
      password: connector?.connectorDetails?.Password ?? {
        value: "",
        isSensitive: true,
        hasValue: false,
      },
      reportName: connector?.connectorDetails?.ReportName ?? "",
      folderName: connector?.connectorDetails?.FolderName ?? "",
    };
  };

  public get type(): string {
    return Five9Connector.Type;
  }

  public get defaultValues(): Five9FormValues {
    return {
      username: { value: "", isSensitive: false, hasValue: false },
      password: { value: "", isSensitive: false, hasValue: false },
      reportName: "",
      folderName: "",
    };
  }

  public renderFormFields(
    register: UseFormRegister<ConnectorWizardFormValues>,
    _: Control<ConnectorWizardFormValues>,
    errors?: FieldErrors<ConnectorWizardFormValues>
  ): ReactNode {
    return (
      <Five9ConfigForm
        defaultValues={this.defaultValues}
        register={register}
        errors={errors}
      />
    );
  }
}
